import styled from 'styled-components';
import { Form } from 'formik';

import { placeholderAnimate } from 'utils/style/animations';
import { convertNumbersToProperFormat } from 'utils/number';
import {
  red,
  blue,
  green,
  ocean,
  selected,
  black,
  boxShadow1,
  gray,
  exactWhite,
} from 'utils/style/colors';
import { languageDirection } from '../../../../../../i18n';

interface IInputGroup {
  hide: boolean;
  count: number;
}

interface IQuestionProps {
  isActive: boolean;
}

interface ICanIFillProps {
  status: CanIFillStatus;
}

interface IUniqueBoxLabelProps {
  isActive: boolean;
}

export type CanIFillStatus = 'idle' | 'allow' | 'reject';

const getBackgroundColor: { [key in CanIFillStatus]: string } = {
  idle: blue,
  allow: green,
  reject: red,
};

export const Container = styled.div`
  width: 100%;
  margin: 24px 0;
  display: flex;
  overflow: visible;
  flex-flow: column;
`;

export const UniqueBoxHeader = styled.div`
  color: ${black};
  display: flex;
  flex-grow: 1;
  align-items: center;
  font-weight: 500;
  justify-content: space-between;
`;

export const Question = styled.div<IQuestionProps>`
  width: 100%;
  margin-bottom: 10px;
  ${({ isActive }) =>
    isActive &&
    `
    // border: 1px #96b0b7 solid;
    // ${UniqueBoxHeader} {
    //   color: var(--exact-white);
    //   background-color: #96b0b7;
    // }
  `}
`;

export const UniqueBoxLabel = styled('label')<IUniqueBoxLabelProps>`
  height: 100% !important;
  display: flex !important;
  align-items: center;
  justify-content: flex-start;
  color: ${(props) => (props.checked ? 'var(--exact-white)' : '')} !important;
`;

export const InputGroup = styled('div')<IInputGroup>`
  width: 200px;
  height: 3.5rem;
  display: block;
  position: relative;
  box-shadow: ${boxShadow1};
  margin-inline-end: 12px;
  border-radius: 1rem;
  margin-bottom: 12px;
  background-color: var(--exact-white);

  &:hover {
    background-color: #dcdcdc;
  }

  & > ${UniqueBoxLabel} {
    width: 100%;
    height: 100% !important;
    align-items: center;
    text-align: center;
    z-index: 2;
    display: flex !important;
    padding: ${(props) =>
      props.hide ? '0.3rem 0' : '0.3rem 1.5rem 0.3rem 3rem'} !important;
    //text-align: center;
    overflow: hidden;
    position: relative !important;
    font-size: 13px !important;
    transition: color 0.2s ease-in !important;
    border-radius: 18px;
    justify-content: ${(props) =>
      props.hide ? 'center' : 'flex-start'} !important;

    &:before {
      opacity: 0;
      content: '';
      z-index: -1 !important;
      position: absolute;
      transition: color 100ms ease-out !important;
      border-radius: 50%;
      transform: translate(50%, -50%) scale3d(1, 1, 1);
      -webkit-transform: translate(50%, -50%) scale3d(1, 1, 1);
      background-color: ${ocean};
    }

    &:after {
      top: 50% !important;
      left: 4px !important;
      right: auto !important;
      min-width: 27px !important;
      color: ${(props) =>
        `${props.count}` === '0'
          ? '#b5b5cc !important'
          : `${ocean} !important`};
      margin: 0 !important;
      height: 25px !important;
      border: ${(props) =>
        `${props.count}` === '0'
          ? '2px solid #b5b5cc !important'
          : `2px solid ${ocean} !important`};
      position: absolute !important;
      transform: translateY(-50%) !important;
      -webkit-transform: translateY(-50%) !important;
      font-size: 14px !important;
      text-align: center !important;
      transition: color 200ms ease-in !important;
      align-items: center;
      font-weight: 400 !important;
      border-radius: 13.5px !important;
      justify-content: center;
      background-color: var(--exact-white) !important;
      content: ${(props) =>
        `'${convertNumbersToProperFormat(props.count)}'`} !important;
      display: ${(props) => (props.hide ? 'none' : 'table')} !important;
      padding: 0px 3px;
    }
  }

  & > input {
    top: 50%;
    left: 6px;
    order: 1;
    width: 26px;
    height: 26px;
    cursor: pointer;
    z-index: 2;
    position: absolute;
    visibility: hidden;
    transform: translateY(-50%);
    -webkit-transform: translateY(-50%);

    &:checked ~ ${UniqueBoxLabel} {
      color: var(--exact-white) !important;
      background-color: ${selected} !important;
      &:before {
        opacity: 1;
        transform: translate(50%, -50%) scale3d(56, 56, 1);
        -webkit-transform: translate(50%, -50%) scale3d(56, 56, 1);
      }

      &:after {
        border-color: var(--exact-white) !important;
        background-color: var(--exact-white);
      }
    }
  }
`;

export const Wrapper = styled.div`
  background-color: var(--exact-white);
  width: 100%;
  display: flex;
  padding: 1.5rem 1.5rem 0.6rem 1.5rem;
  flex-flow: wrap;
  font-weight: 600 !important;
  justify-content: center;
`;

export const DistinctiveForm = styled(Form)`
  margin: 2rem 0 0;
  display: flex;
  overflow: auto;
  flex-direction: column;
  & > :last-child {
    margin-bottom: 0;
  }
`;

export const DuplicateAnswerIcon = styled('span')`
  flex: none;
  width: 2.5rem;
  height: 2.5rem;
  display: inline-flex;
  padding: 0.5rem;
  transition: color 0.3s ease-in;
  align-items: center;
  justify-content: center;
  ${(_) =>
    languageDirection() === 'rtl'
      ? 'border-top-right-radius: 4px;border-bottom-right-radius: 4px;'
      : 'border-top-left-radius: 4px;border-bottom-left-radius: 4px;'}
  & > * {
    color: ${gray} !important;
    font-size: 19px !important;
    font-weight: bold !important;
  }
`;

export const DistinctiveHintText = styled('span')`
  width: 100%;
  margin: auto;
  display: flex;
  font-weight: 400;
  justify-content: center;
`;

export const CanIFillLoading = styled('span')`
  width: inherit;
  height: 2.5rem;
  display: inline-flex;
  overflow: hidden;
  position: relative;
  animation: ${placeholderAnimate} 1s linear forwards infinite;
  background: #f6f7f8;
  background: linear-gradient(
    to right,
    var(--exact-white) 2%,
    #f2f5f7 18%,
    var(--exact-white) 33%
  );
  align-items: center;
  background-size: 1300px;
  justify-content: center;
  border-end-end-radius: 5px;
  border-start-end-radius: 5px;
  ::after {
    content: 'در حال بررسی...';
    font-weight: 400;
  }
`;

export const CanIFillContainer = styled('div')<ICanIFillProps>`
  width: 24rem;
  border: ${(props) => `1px solid ${getBackgroundColor[props.status]}`};
  display: flex;
  align-self: center;
  transition: all 0.2s ease;
  margin-top: 2rem;
  align-items: center;
  background-color: var(--exact-white);
  border-radius: 0.5rem;
  & ${DuplicateAnswerIcon} {
    background: ${(props) => getBackgroundColor[props.status]};
  }
  & ${DistinctiveHintText} {
    color: ${(props) => getBackgroundColor[props.status]};
  }
`;

export const IndexOrder = styled.span`
  width: 2rem;
  height: 2rem;
  padding: 0.25rem;
  border-radius: 10rem;
  text-align: center;
  display: inline-block;
  background-color: var(--exact-white);
  color: ${gray};
  margin-inline-end: 0.5rem;
`;

export const InputContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: stretch;
  width: 200px;
  min-height: 3.5rem;
  margin: 0 1rem 1rem 0;
  box-shadow: ${boxShadow1};
  border-radius: 1rem;
`;

export const InputHeader = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-inline-end: ${(props) => (props.hideCounts ? '0rem' : '-3rem')};
  background: ${(props) => (!props.checked ? exactWhite : `${props.color}`)};
  border-radius: 1rem;
  z-index: 100;
  border: ${(props) => (!props.checked ? `0.05rem solid ${gray}` : 'none')};
`;
export const InputHeaderContent = styled.div`
  flex: 1;
  padding-inline-start: 0.5rem;
  height: 100%;
  & > input {
    cursor: pointer;
  }

  & > label {
    width: 100%;
    padding: 0 0.5rem 0 !important;
    color: ${(props) => (props.checked ? '#fff' : black)} !important;

    &::before {
      content: '';
      visibility: hidden !important;
    }

    &::after {
      padding: 0 !important;
    }
  }
`;
export const CounterBadge = styled.div`
  min-width: 6rem;
`;
export const CounterBadgeContent = styled.div`
  height: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  background-image: linear-gradient(
    ${(props) => (props.theme.direction === 'rtl' ? 'to right' : 'to left')},
    ${(props) => props.color},
    var(--exact-white)
  );
  background: ${(props) => props.checked && props.color};
  color: #fff;
  padding-inline-end: 1rem;
  padding-inline-start: 1.5rem;
  border-end-end-radius: 1rem;
  border-start-end-radius: 1rem;
  position: relative;
  z-index: ${(props) => props.checked && '101'};
`;
